import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { environment } from '@environments/environment';
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  User,
  updateProfile,
  sendEmailVerification,
  updatePassword,
  validatePassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { getFirestore, setDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { NGXLogger } from 'ngx-logger';
import { from, Subject } from 'rxjs';

const firebaseConfig = {
  apiKey: environment['FIREBASE_API_KEY'] || '',
  authDomain: environment['FIREBASE_AUTH_DOMAIN'] || '',
  projectId: environment['FIREBASE_PROJECT_ID'] || '',
  storageBucket: environment['FIREBASE_STORAGE_BUCKET'] || '',
  messagingSenderId: environment['FIREBASE_MESSAGING_SENDER_ID'] || '',
  appId: environment['FIREBASE_APP_ID'] || '',
  measurementId: environment['FIREBASE_MEASUREMENT_ID'] || '',
};

const PK_COLLECTION = 'privateKeys';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  app = initializeApp(firebaseConfig);
  auth = getAuth(this.app);
  analytics = getAnalytics(this.app);
  firestore = getFirestore(this.app);

  signedUser = new Subject<User | null>();

  constructor(private logger: NGXLogger) {
    // web auth logic
    if (Capacitor.getPlatform() === 'web') {
      onAuthStateChanged(this.auth, user => {
        if (user) {
          this.signedUser.next(user);
        } else {
          this.signedUser.next(null);
        }
      });
    }
  }

  // only for web auth
  subscribeToAuthChanges() {
    return this.signedUser.asObservable();
  }

  currentUser() {
    return this.auth.currentUser;
  }

  currentMobileUser() {
    return from(FirebaseAuthentication.getCurrentUser());
  }

  getIdTokenMobile() {
    return from(FirebaseAuthentication.getIdToken());
  }

  // If the new account was created, the user is signed in automatically
  // check if user exist need via catch errors from this call
  // error codes https://firebase.google.com/docs/reference/js/auth?authuser=0&hl=ru#autherrorcodes
  // BUT! for password validation we need to use validatePassword
  createUserWithEmailAndPassword(email: string, password: string) {
    this.logger.trace('create user with email and password', email);
    return from(createUserWithEmailAndPassword(this.auth, email, password));
  }

  addPrivateKey(userId: string, encryptedPrivateKey: string) {
    this.logger.trace('addPrivateKey method');
    return from(setDoc(doc(this.firestore, PK_COLLECTION, userId), { encryptedPrivateKey }));
  }

  getPrivateKeys(userId: string) {
    this.logger.trace('getPrivateKeys method');
    const docRef = doc(this.firestore, PK_COLLECTION, userId);
    return from(getDoc(docRef));
  }

  signInGoogleWeb() {
    return from(signInWithPopup(this.auth, new GoogleAuthProvider()));
  }

  signInGoogleMobile() {
    return from(FirebaseAuthentication.signInWithGoogle());
  }

  signInAppleMobile() {
    return from(FirebaseAuthentication.signInWithApple());
  }

  updatePrivateKey(userId: string, encryptedPrivateKey: string) {
    this.logger.trace('updatePrivateKey method');
    const docRef = doc(this.firestore, PK_COLLECTION, userId);
    return from(
      updateDoc(docRef, {
        encryptedPrivateKey,
      }),
    );
  }

  // it is not necessary but it is better to use this method to validate the password
  validatePassword(passwordFromUser: string) {
    return from(validatePassword(this.auth, passwordFromUser));
  }

  signInWithEmailAndPassword(email: string, password: string) {
    this.logger.trace('sign in user with email and password', email);
    return from(signInWithEmailAndPassword(this.auth, email, password));
  }

  signOut() {
    if (Capacitor.getPlatform() === 'web') {
      return this.signOutWeb();
    } else {
      return this.signOutMobile();
    }
  }

  signOutWeb() {
    this.logger.trace('sign out user');
    return from(signOut(this.auth));
  }

  signOutMobile() {
    this.logger.trace('sign out user');
    return from(FirebaseAuthentication.signOut());
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  // need to use name and pic instead of stored on-chain (account avatar and name)
  updateProfile(
    user: User,
    {
      displayName,
      photoURL,
    }: {
      displayName?: string | null;
      photoURL?: string | null;
    },
  ) {
    return from(updateProfile(user, { displayName, photoURL }));
  }

  // not sure that we will require verification
  sendEmailVerification(user: User) {
    return from(sendEmailVerification(user));
  }

  // ATTENTION!
  // if you encrypt private key with the same password as firebase login
  // you need to very carefly encrypt PK with new password BUT store it in a dedicated DB field
  // and mark user as in an update process
  // after storing the new PK, change firebase password and AFTER that update main PK field
  // otherwise encrypted data can be unsynced with firebase password and you will lost data!
  updatePassword(user: User, newPassword: string) {
    return from(updatePassword(user, newPassword));
  }
}
